import { getMsalInstance, loginRequestScopes } from '@/core/msal/msal.config';
import { ISocialAuthCredential, ISocialAuthUser } from '../../domain/interfaces/social-auth.interface';
import { logger } from '@/core/utils/logger';
import type { AuthenticationResult } from '@azure/msal-browser';

/**
 * Datasource para autenticacion Microsoft via MSAL (Azure AD directo).
 *
 * Usa **redirect flow** (`loginRedirect` + `handleRedirectPromise`) en lugar
 * de popup. Razones:
 *   1. El popup polling de MSAL produce `timed_out` en algunos browsers/redes
 *      cuando el parent no alcanza a leer el hash del popup antes del cierre.
 *   2. Redirect flow es más robusto en Safari/Edge con ITP estricto y en
 *      browsers con popup blockers agresivos.
 *
 * Flujo:
 *   1. `initiateMicrosoftSignIn()` navega TODA la pestaña a Microsoft.
 *   2. Tras autenticar, Microsoft redirige a `${origin}/auth/redirect#code=...`.
 *   3. `processRedirectResponse()` (llamado en `/auth/redirect`) extrae el
 *      `code`, intercambia por tokens, y devuelve la credencial.
 */
export class MsalAuthDataSource {
  /**
   * Inicia el flujo de login Microsoft via redirect. La pestaña actual
   * navega a `login.microsoftonline.com`; esta función NO retorna en
   * el sentido habitual (la página deja de existir en cuanto MSAL navega).
   *
   * El callback se procesa en `/auth/redirect` vía `processRedirectResponse`.
   */
  async initiateMicrosoftSignIn(): Promise<void> {
    try {
      const msal = getMsalInstance();
      await msal.initialize();

      // Drenar cualquier respuesta MSAL pendiente y limpiar el flag interno
      // `interaction.status`. Sin esto, si un flujo previo falló sin pasar
      // por `/auth/redirect`, MSAL rechaza el nuevo `loginRedirect` con
      // `interaction_in_progress`. Idempotente: si no hay flujo pendiente,
      // resuelve null en milisegundos.
      await msal.handleRedirectPromise({ navigateToLoginRequestUrl: false }).catch(() => {
        // Si el drenaje falla (estado corrupto), seguimos: el `loginRedirect`
        // siguiente sobrescribe el state. Mejor que abortar el flujo.
      });

      // Limpiar cualquier cuenta previa para forzar selección de cuenta.
      msal.setActiveAccount(null);

      // `loginRedirect` navega la pestaña — la promesa nunca resuelve en este
      // contexto. Devuelve void; cualquier error de configuración (clientId
      // faltante, etc.) se lanza sincrónicamente antes de la navegación.
      await msal.loginRedirect({
        scopes: loginRequestScopes,
        prompt: 'select_account',
      });
    } catch (error: unknown) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error iniciando login Microsoft (MSAL redirect):', normalizedError, { module: 'auth', action: 'msalInitiateRedirect' });
      throw error;
    }
  }

  /**
   * Procesa la respuesta de Microsoft tras el redirect. Llamado desde la
   * página `/auth/redirect`. Devuelve `null` si la URL actual no contiene
   * una respuesta MSAL pendiente (caso navegación directa a la página).
   */
  async processRedirectResponse(): Promise<ISocialAuthCredential | null> {
    try {
      const msal = getMsalInstance();
      await msal.initialize();

      // `navigateToLoginRequestUrl: false` evita que MSAL haga su propia
      // navegación tras procesar el callback. Sin esto MSAL intenta volver
      // al URL donde se llamó `loginRedirect` (`/auth/login`) y compite con
      // nuestro `router.replace('/dashboard')` desde la redirect page,
      // abortando el flujo en el medio (síntoma observado: no llega al
      // POST de token exchange contra `/oauth2/v2.0/token`).
      const result: AuthenticationResult | null = await msal.handleRedirectPromise({
        navigateToLoginRequestUrl: false,
      });

      if (!result || !result.idToken) {
        return null;
      }

      msal.setActiveAccount(result.account);

      const account = result.account;
      const user: ISocialAuthUser = {
        uid: account?.homeAccountId || account?.localAccountId || '',
        email: account?.username || '',
        displayName: account?.name || '',
        photoURL: '',
      };

      return {
        user,
        accessToken: result.accessToken || '',
        idToken: result.idToken,
        providerId: 'microsoft.com',
        tenantId: account?.tenantId,
      };
    } catch (error: unknown) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error procesando callback MSAL:', normalizedError, { module: 'auth', action: 'msalProcessRedirect' });
      throw error;
    }
  }

  /**
   * Cierra sesión en MSAL via redirect. La pestaña navega a Microsoft
   * para invalidar la sesión y vuelve a `postLogoutRedirectUri`.
   */
  async signOut(): Promise<void> {
    try {
      const msal = getMsalInstance();
      await msal.initialize();
      await msal.logoutRedirect();
    } catch (error: unknown) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error al cerrar sesión MSAL:', normalizedError, {
        module: 'auth',
        action: 'msalSignOut',
      });
    }
  }
}
